import { type FC, useEffect, useRef } from "react";
import {
	MetaAdsDistanceUnit,
	MetaAdsGeoTargetingMode,
	MetaAdsLocationType,
} from "shared";
import { Api } from "../../api.config";
import { Select } from "../../components/Select";
import { useCampaignWizard } from "../CampaignWizardContext";
import { useDeliveryEstimate } from "../useDeliveryEstimate";
import { useGeoTargeting } from "../useGeoTargeting";
import { AudienceEstimate } from "./AudienceEstimate";
import { CityRegionPanel } from "./CityRegionPanel";
import { ExclusionPanel } from "./ExclusionPanel";
import { GeoTargetingMap } from "./GeoTargetingMap";
import { GeoTemplateManager } from "./GeoTemplateManager";
import { LocationPinList } from "./LocationPinList";
import { LocationSearch } from "./LocationSearch";
import { ZipCodePanel } from "./ZipCodePanel";

const MODE_OPTIONS = [
	{ value: MetaAdsGeoTargetingMode.RADIUS_PIN, label: "Radius Pins" },
	{ value: MetaAdsGeoTargetingMode.ZIP_CODE, label: "Zip Codes" },
	{ value: MetaAdsGeoTargetingMode.CITY_REGION, label: "City / Region / DMA" },
];

const LOCATION_TYPE_OPTIONS: Array<{
	value: MetaAdsLocationType;
	label: string;
}> = [
	{
		value: MetaAdsLocationType.HOME_RECENT,
		label: "People living in or recently in this location",
	},
	{
		value: MetaAdsLocationType.HOME,
		label: "People living in this location",
	},
	{
		value: MetaAdsLocationType.RECENT,
		label: "People recently in this location",
	},
	{
		value: MetaAdsLocationType.TRAVEL_IN,
		label: "People traveling in this location",
	},
];

export const GeoTargetingSection: FC = () => {
	const { draft } = useCampaignWizard();
	const {
		geo,
		mode,
		setMode,
		addPin,
		removePin,
		updatePin,
		canAddPin,
		exclusions,
		addExclusionPin,
		setLocationType,
	} = useGeoTargeting();

	const { data: estimate, isLoading: estimateLoading } = useDeliveryEstimate(
		geo,
		draft.optimizationGoal,
		draft.ageMin,
		draft.ageMax,
		draft.genders,
	);

	// GBP auto-populate on first render
	const gbpAttempted = useRef(false);
	useEffect(() => {
		if (gbpAttempted.current) return;
		if (geo.locationPins.length > 0) return;
		gbpAttempted.current = true;

		(async () => {
			try {
				const locationsRes = await Api.GoogleMyBusinessGetLocations();
				const locations = locationsRes.data;
				if (!locations || locations.length === 0) return;

				// Prefer the GBP location selected for this account; fall back to
				// the first location when none has been selected.
				const selected = (await Api.GoogleMyBusinessGetSelectedLocation()).data;
				const gbpLocation =
					locations.find(({ id }) => id === selected?.id) ?? locations[0];
				const detailRes = await Api.GoogleMyBusinessGetLocation(gbpLocation.id);
				const detail = detailRes.data;
				const lat = detail?.latlng?.latitude;
				const lng = detail?.latlng?.longitude;
				if (lat && lng) {
					addPin({
						label: gbpLocation.title || "Business Location",
						latitude: lat,
						longitude: lng,
						radius: 10,
						distanceUnit: MetaAdsDistanceUnit.MILE,
					});
				}
			} catch {
				// GBP not connected or error — silently skip
			}
		})();
	}, [addPin, geo.locationPins.length]);

	const hasTargeting =
		geo.locationPins.length > 0 ||
		(geo.zipCodes?.length ?? 0) > 0 ||
		(geo.cities?.length ?? 0) > 0 ||
		(geo.regions?.length ?? 0) > 0 ||
		(geo.dmas?.length ?? 0) > 0 ||
		(geo.countries?.length ?? 0) > 0;

	return (
		<div>
			<h6>Geo-Targeting</h6>
			<div
				style={{
					display: "flex",
					gap: 16,
					minHeight: 440,
				}}
			>
				{/* Left panel */}
				<div
					style={{
						width: 360,
						flexShrink: 0,
						display: "flex",
						flexDirection: "column",
						gap: 12,
						overflowY: "auto",
						maxHeight: 560,
					}}
				>
					{/* Mode selector */}
					<div className="btn-group btn-group-sm w-100">
						{MODE_OPTIONS.map((opt) => (
							<button
								key={opt.value}
								type="button"
								className={`btn ${mode === opt.value ? "btn-primary" : "btn-outline-secondary"}`}
								onClick={() => setMode(opt.value)}
							>
								{opt.label}
							</button>
						))}
					</div>

					{/* Mode-specific panel */}
					{mode === MetaAdsGeoTargetingMode.RADIUS_PIN && (
						<>
							<LocationSearch canAddPin={canAddPin} onAddPin={addPin} />
							<LocationPinList
								pins={geo.locationPins}
								onUpdatePin={updatePin}
								onRemovePin={removePin}
							/>
						</>
					)}

					{mode === MetaAdsGeoTargetingMode.ZIP_CODE && <ZipCodePanel />}

					{mode === MetaAdsGeoTargetingMode.CITY_REGION && <CityRegionPanel />}

					<div>
						<label htmlFor="location-type" className="form-label small mb-1">
							Location targeting type
						</label>
						<Select
							id="location-type"
							className="w-100"
							size="sm"
							value={geo.locationType ?? MetaAdsLocationType.HOME_RECENT}
							onChange={(e) =>
								setLocationType(e.target.value as MetaAdsLocationType)
							}
						>
							{LOCATION_TYPE_OPTIONS.map((opt) => (
								<option key={opt.value} value={opt.value}>
									{opt.label}
								</option>
							))}
						</Select>
					</div>

					<ExclusionPanel />

					<GeoTemplateManager />

					<AudienceEstimate
						lower={estimate?.lower}
						upper={estimate?.upper}
						audienceLower={estimate?.audienceLower}
						audienceUpper={estimate?.audienceUpper}
						isLoading={estimateLoading}
						hasTargeting={hasTargeting}
					/>
				</div>

				{/* Right panel - Map */}
				<div style={{ flex: 1, minWidth: 0 }}>
					<GeoTargetingMap
						pins={geo.locationPins}
						canAddPin={mode === MetaAdsGeoTargetingMode.RADIUS_PIN && canAddPin}
						onAddPin={(lat, lng) => {
							addPin({
								label: `${lat.toFixed(4)}, ${lng.toFixed(4)}`,
								latitude: lat,
								longitude: lng,
								radius: 10,
								distanceUnit: MetaAdsDistanceUnit.MILE,
							});
						}}
						onPinDrag={(pinId, lat, lng) => {
							updatePin(pinId, {
								latitude: lat,
								longitude: lng,
								label: `${lat.toFixed(4)}, ${lng.toFixed(4)}`,
							});
						}}
						mode={mode}
						exclusions={exclusions}
						onAddExclusionPin={(lat, lng) => {
							addExclusionPin({
								label: `${lat.toFixed(4)}, ${lng.toFixed(4)}`,
								latitude: lat,
								longitude: lng,
								radius: 5,
								distanceUnit: MetaAdsDistanceUnit.MILE,
							});
						}}
					/>
				</div>
			</div>
		</div>
	);
};
